'use client';

import {
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import type { StepProps } from '@/components/wizard/StepProps';

import type { AddConnectorWizardState } from '../addConnectorWizardState';

const CONNECTOR_TYPE_IDS = [
  'AWS_ACCOUNT_CONNECTOR',
  'GCP_PROJECT_CONNECTOR',
] as const;

type ConnectorTypeId = (typeof CONNECTOR_TYPE_IDS)[number];

const CONNECTOR_TYPES: {
  id: ConnectorTypeId;
  label: string;
  description: string;
}[] = [
  {
    id: 'AWS_ACCOUNT_CONNECTOR',
    label: 'AWS Account',
    description:
      'Connect to an AWS account using IAM roles for discovery and data access',
  },
  {
    id: 'GCP_PROJECT_CONNECTOR',
    label: 'GCP Project',
    description: 'Connect to a GCP project using a service account',
  },
];

function isConnectorTypeId(value: string): value is ConnectorTypeId {
  return (CONNECTOR_TYPE_IDS as readonly string[]).includes(value);
}

export const ConnectorTypeStep = (
  props: StepProps<AddConnectorWizardState>
) => {
  const { connectorType, connectorName } = props.wizardState;

  const handleNext = () => {
    if (connectorType === 'AWS_ACCOUNT_CONNECTOR') {
      props.setNextStep(props.currentStep.next?.[0]);
    } else {
      props.setNextStep(props.currentStep.next?.[1]);
    }
  };

  return (
    <Stack sx={{ height: '100%' }} alignItems='center' justifyContent='center'>
      <Stack width='400px' gap='40px'>
        <Stack gap='16px'>
          <FormControl size='small' sx={{ flex: 1 }}>
            <FormLabel>Name</FormLabel>
            <OutlinedInput
              autoFocus
              sx={{ padding: '8px' }}
              placeholder='e.g., AWS Account 123456789012'
              value={connectorName}
              onChange={(e) =>
                props.setWizardState({
                  ...props.wizardState,
                  connectorName: e.target.value,
                })
              }
            />
          </FormControl>
        </Stack>

        <FormControl size='small' sx={{ flex: 1 }}>
          <FormLabel>Type</FormLabel>
          <Select
            sx={{ paddingLeft: '8px' }}
            size='medium'
            value={connectorType}
            onChange={(e) => {
              const val = e.target.value;
              if (isConnectorTypeId(val)) {
                props.setWizardState({
                  ...props.wizardState,
                  connectorType: val,
                });
              }
            }}
            renderValue={(value) => {
              return CONNECTOR_TYPES.find((ct) => ct.id === value)?.label;
            }}
          >
            {CONNECTOR_TYPES.map((ct) => (
              <MenuItem key={ct.id} value={ct.id}>
                <Stack gap='4px' padding='4px'>
                  <Typography variant='body1'>{ct.label}</Typography>
                  <Typography variant='subtitle2' color='text.secondary'>
                    {ct.description}
                  </Typography>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant='contained'
          disabled={!connectorName.trim()}
          onClick={handleNext}
          sx={{ height: '48px' }}
        >
          Continue
          <i className='material-symbols-arrow-right-alt ml-[8px]' />
        </Button>
      </Stack>
    </Stack>
  );
};
